import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  query,
  serverTimestamp,
  setDoc,
  Timestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { db } from '../firebase';

export const getProfileDisclosureId = (ownerId, viewerId, contextId) => (
  `${ownerId}_${viewerId}_${contextId}`.replace(/[^\w-]/g, '_')
);

export const getDisclosureExpiresAt = (recruitment) => {
  if (recruitment?.recruitmentType !== 'single' || !recruitment.departureDate) {
    return null;
  }

  const departureDate = recruitment.departureDate.toDate
    ? recruitment.departureDate.toDate()
    : new Date(recruitment.departureDate);
  if (Number.isNaN(departureDate.getTime())) return null;

  const expiresAt = new Date(departureDate);
  expiresAt.setHours(24, 0, 0, 0);
  return Timestamp.fromDate(expiresAt);
};

export const createProfileDisclosure = async ({
  ownerId,
  viewerId,
  chatId,
  recruitmentId = '',
  recruitment = null,
  createdByAction = 'manual_share',
}) => {
  if (!ownerId || !viewerId || !chatId || ownerId === viewerId) return;

  const disclosureId = getProfileDisclosureId(ownerId, viewerId, chatId);
  await setDoc(doc(db, 'profileDisclosures', disclosureId), {
    ownerId,
    viewerId,
    chatId,
    recruitmentId,
    active: true,
    expiresAt: getDisclosureExpiresAt(recruitment),
    createdByAction,
    revokedReason: '',
    lastValidatedAt: serverTimestamp(),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  }, { merge: true });
};

export const revokeProfileDisclosure = async ({
  ownerId,
  viewerId,
  chatId,
  revokedReason = 'manual_revoke',
}) => {
  if (!ownerId || !viewerId || !chatId || ownerId === viewerId) return;

  const disclosureId = getProfileDisclosureId(ownerId, viewerId, chatId);
  await updateDoc(doc(db, 'profileDisclosures', disclosureId), {
    active: false,
    revokedReason,
    updatedAt: serverTimestamp(),
    lastValidatedAt: serverTimestamp(),
  });
};

export const isProfileDisclosureValid = async (disclosure) => {
  if (!disclosure?.active) return false;

  const expiresAt = disclosure.expiresAt?.toDate
    ? disclosure.expiresAt.toDate()
    : disclosure.expiresAt ? new Date(disclosure.expiresAt) : null;
  if (expiresAt && expiresAt <= new Date()) return false;

  if (disclosure.chatId?.startsWith('group_')) {
    const chatSnap = await getDoc(doc(db, 'chats', disclosure.chatId));
    if (!chatSnap.exists()) return false;
    const chat = chatSnap.data();
    const participants = chat.participants || [];
    return chat.status !== 'dissolved'
      && participants.includes(disclosure.ownerId)
      && participants.includes(disclosure.viewerId);
  }

  if (disclosure.recruitmentId && disclosure.chatId) {
    const chatSnap = await getDoc(doc(db, 'chats', disclosure.chatId));
    if (!chatSnap.exists()) return false;

    const chat = chatSnap.data();
    const participants = chat.participants || [];
    if (
      chat.status === 'dissolved'
      || !participants.includes(disclosure.ownerId)
      || !participants.includes(disclosure.viewerId)
    ) {
      return false;
    }

    const organizerId = chat.organizerId;
    const applicantId = disclosure.ownerId === organizerId
      ? disclosure.viewerId
      : disclosure.viewerId === organizerId
        ? disclosure.ownerId
        : '';
    if (!applicantId) return false;

    const applicationsSnap = await getDocs(
      query(
        collection(db, 'applications'),
        where('recruitmentId', '==', disclosure.recruitmentId),
        where('userId', '==', applicantId),
        limit(1)
      )
    );
    if (applicationsSnap.empty) return false;

    const applicationStatus = applicationsSnap.docs[0].data()?.applicationStatus || 'pending';
    return applicationStatus !== 'rejected';
  }

  return true;
};
